/**
 * <s-notification> — Notification/Toast Web Component
 * Manages toast creation, stacking, auto-dismiss, and pause-on-hover.
 * Listen for `s-notification:add` events on `document` to create toasts.
 *
 * Accessibility:
 * - Each toast includes a visible close button with aria-label="Dismiss"
 * - Pressing Escape dismisses the most recent toast
 * - Toasts use role="status" (info/success) or role="alert" (error)
 * - Focus is not stolen from the user's current action
 */
class SNotification extends HTMLElement {
  #toasts = new Map();
  #isPaused = false;
  #ac;

  connectedCallback() {
    const pos = this.getAttribute('position') || 'bottom-right';
    this.dataset.position = pos;
    this.setAttribute('role', 'status');
    this.setAttribute('aria-live', 'polite');

    this.#ac = new AbortController();
    const opts = { signal: this.#ac.signal };

    document.addEventListener('s-notification:add', (e) => {
      const { message, variant = 'info', duration } = e.detail || {};
      if (!message) return;
      this.#createToast(message, variant, duration);
    }, opts);

    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && this.#toasts.size > 0) {
        const toasts = [...this.#toasts.keys()];
        this.#dismiss(toasts[toasts.length - 1]);
      }
    }, opts);

    this.addEventListener('mouseenter', () => this.#pauseAll(), opts);
    this.addEventListener('mouseleave', () => this.#resumeAll(), opts);
    this.addEventListener('focusin', () => this.#pauseAll(), opts);
    this.addEventListener('focusout', (e) => {
      if (!this.contains(e.relatedTarget)) this.#resumeAll();
    }, opts);
  }

  disconnectedCallback() {
    this.#ac?.abort();
    for (const [, state] of this.#toasts) clearTimeout(state.timeoutId);
    this.#toasts.clear();
  }

  get #defaultDuration() {
    return parseInt(this.getAttribute('duration'), 10) || 5000;
  }

  #createToast(message, variant, duration) {
    const dur = duration ?? this.#defaultDuration;
    const toast = document.createElement('div');
    toast.className = `notification notification-${variant}`;
    toast.setAttribute('role', variant === 'error' ? 'alert' : 'status');
    toast.setAttribute('aria-atomic', 'true');

    const text = document.createElement('span');
    text.className = 'notification-message';
    text.textContent = message;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'notification-close';
    closeBtn.setAttribute('aria-label', this.dataset.dismissLabel || 'Dismiss');
    closeBtn.textContent = '\u2715'; // × (multiplication sign used as close icon)
    closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      this.#dismiss(toast);
    }, { signal: this.#ac.signal });

    toast.append(text, closeBtn);
    toast.addEventListener('click', () => this.#dismiss(toast), { signal: this.#ac.signal });
    this.appendChild(toast);

    const state = { remainingTime: dur, timeoutId: null, startTime: Date.now() };
    if (dur > 0 && !this.#isPaused) {
      state.timeoutId = setTimeout(() => this.#dismiss(toast), dur);
    }
    this.#toasts.set(toast, state);
  }

  async #dismiss(toast) {
    const state = this.#toasts.get(toast);
    if (!state) return;
    clearTimeout(state.timeoutId);
    this.#toasts.delete(toast);
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      try {
        await toast.animate(
          [{ opacity: 1, transform: 'translateY(0)' }, { opacity: 0, transform: 'translateY(8px)' }],
          { duration: 150, easing: 'cubic-bezier(0.16, 1, 0.3, 1)' }
        ).finished;
      } catch (e) {
        if (e.name !== 'AbortError') throw e;
        return;
      }
    }
    toast.remove();
  }

  #pauseAll() {
    if (this.#isPaused) return;
    this.#isPaused = true;
    for (const [, state] of this.#toasts) {
      if (state.timeoutId) {
        clearTimeout(state.timeoutId);
        state.timeoutId = null;
        state.remainingTime = Math.max(0, state.remainingTime - (Date.now() - state.startTime));
      }
    }
  }

  #resumeAll() {
    if (!this.#isPaused) return;
    this.#isPaused = false;
    for (const [toast, state] of this.#toasts) {
      if (state.remainingTime <= 0) {
        this.#dismiss(toast);
      } else if (!state.timeoutId) {
        state.startTime = Date.now();
        state.timeoutId = setTimeout(() => this.#dismiss(toast), state.remainingTime);
      }
    }
  }
}

customElements.define('s-notification', SNotification);
